import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toUserMessage } from '@platform/api-client';
import { LuCircleCheck } from '@platform/icon';

import { Alert } from '@/shared/ui';
import { AccountDeviceList, useMyDevices } from '@/features/devices';
import { DESKTOP_QUERY } from '../lib/authorize-params';

/**
 * 데스크톱 로그인 전용 기기 정리 — `/desktop/devices`.
 *
 * 등록 PC 가 가득 차 동의 화면이 허용을 막았을 때 온다. 여기서는 **해제만** 한다 —
 * 새 PC 등록은 XCOST 에서 다시 로그인할 때 원문 fingerprint 로 이뤄진다(웹은 원문을 모른다).
 * 인가 파라미터를 받지 않으므로 이 화면에서 프로그램으로 돌려보내지 않는다.
 */
export function DesktopDevicesView() {
  const [searchParams] = useSearchParams();
  const deviceLabel = searchParams.get(DESKTOP_QUERY.DEVICE_LABEL)?.trim() || null;
  const { data, isLoading, isError, error } = useMyDevices();

  if (isLoading) {
    return (
      <Shell title="등록된 PC 정리">
        <p className="py-10 text-center text-sm text-adobe-muted">불러오는 중…</p>
      </Shell>
    );
  }

  if (isError || !data) {
    return (
      <Shell title="등록된 PC 정리">
        <Alert tone="error">{toUserMessage(error)}</Alert>
      </Shell>
    );
  }

  const hasSlot = data.devices.length < data.maxDevices;
  const exhausted = data.release.remaining <= 0;
  const thisPc = deviceLabel ?? '이 PC';

  return (
    <Shell
      title="등록된 PC 정리"
      subtitle={`한 계정에 PC를 최대 ${data.maxDevices}대까지 등록할 수 있습니다. ${thisPc}에서 프로그램을 쓰려면 사용하지 않는 PC를 해제하세요.`}
    >
      <Steps done={hasSlot} />

      {hasSlot ? (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <LuCircleCheck
            className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300"
            aria-hidden
          />
          <div className="text-sm text-emerald-900 dark:text-emerald-100">
            <p className="font-bold">자리가 생겼습니다.</p>
            <p className="mt-1">
              프로그램으로 돌아가 <b>[다시 로그인]</b>을 누르면 {thisPc}가 등록됩니다. 이 창은
              닫아도 됩니다.
            </p>
          </div>
        </div>
      ) : (
        exhausted && (
          <div className="mb-4">
            <Alert tone="warning">
              교체 횟수를 모두 사용해 지금은 PC를 해제할 수 없습니다. 급하게 교체해야 하면
              고객센터로 문의해 주세요.
            </Alert>
          </div>
        )
      )}

      <AccountDeviceList data={data} cleanup />

      <p className="mt-4 text-xs leading-5 text-adobe-muted">
        오래 접속하지 않은 PC부터 보여 줍니다. 해제한 PC는 다시 로그인하면 새로 등록되며,
        그때 다른 PC를 정리해야 할 수 있습니다.
      </p>
    </Shell>
  );
}

function Steps({ done }: { done: boolean }) {
  const steps = ['사용하지 않는 PC 해제', '프로그램에서 다시 로그인'];
  const current = done ? 1 : 0;
  return (
    <ol className="mb-5 flex items-center gap-2 text-xs font-semibold">
      {steps.map((label, i) => (
        <li key={label} className="flex items-center gap-2">
          <span
            className={[
              'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold',
              i < current
                ? 'bg-emerald-600 text-white'
                : i === current
                  ? 'bg-brand-700 text-white'
                  : 'bg-neutral-200 text-neutral-600 dark:bg-white/15 dark:text-neutral-300',
            ].join(' ')}
            aria-hidden
          >
            {i + 1}
          </span>
          <span
            className={
              i === current
                ? 'text-adobe-dark dark:text-neutral-100'
                : 'text-adobe-muted'
            }
          >
            {label}
          </span>
          {i < steps.length - 1 && (
            <span className="mx-1 h-px w-6 bg-neutral-300 dark:bg-white/20" aria-hidden />
          )}
        </li>
      ))}
    </ol>
  );
}

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-lg py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark dark:text-neutral-100">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 text-sm leading-6 text-adobe-muted dark:text-neutral-400">
            {subtitle}
          </p>
        )}
      </header>
      {children}
    </div>
  );
}
